import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import styles from "./Agenda.module.css";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useNavigate } from "react-router-dom";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";

// =====================================================================================
// Ajuste conforme a base real da sua API e a forma como o token é guardado no front.
// No IKONE, pelo que você me passou, o usuário fica salvo em localStorage sob a chave
// usada pelo authSlice do Redux — ajuste getToken() para ler de lá se for diferente.
// =====================================================================================
const API_BASE = `${process.env.REACT_APP_API}/api`;

function getToken(): string | null {
  return localStorage.getItem("token");
}

// Decodifica o payload de um JWT (sem validar assinatura — só para ler claims no front).
function decodificarPayloadJwt(token: string): Record<string, any> | null {
  try {
    const payloadBase64 = token.split(".")[1];
    if (!payloadBase64) return null;
    const normalizado = payloadBase64.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(normalizado)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// =====================================================================================
// Resolve o id do usuário logado tentando, em ordem: (1) claims comuns do JWT gerado
// pelo TokenService, (2) objetos de usuário salvos em localStorage (ex.: authSlice),
// (3) uma chave solta "idUsuario". Ajuste a ordem/chaves se souber exatamente onde o
// front guarda isso — coloquei um console.info para você conferir qual estratégia bateu.
// =====================================================================================
function obterIdUsuarioLogado(): number | null {
  const token = getToken();
  if (token) {
    const payload = decodificarPayloadJwt(token);
    const candidatosClaim = [
      "nameid",
      "id",
      "sub",
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier",
    ];
    for (const claim of candidatosClaim) {
      const valor = payload?.[claim];
      if (valor != null && !Number.isNaN(Number(valor))) {
        console.info(
          `[Agenda] id do usuário resolvido via claim JWT "${claim}":`,
          valor,
        );
        return Number(valor);
      }
    }
  }

  for (const chave of ["_user", "user", "usuario"]) {
    const bruto = localStorage.getItem(chave);
    if (!bruto) continue;
    try {
      const obj = JSON.parse(bruto);
      const id = obj?.id ?? obj?.usuario?.id ?? obj?.user?.id;
      if (id != null && !Number.isNaN(Number(id))) {
        console.info(
          `[Agenda] id do usuário resolvido via localStorage["${chave}"]:`,
          id,
        );
        return Number(id);
      }
    } catch {
      // não era JSON — tenta a próxima chave
    }
  }

  const bruto = localStorage.getItem("idUsuario");
  if (bruto) {
    console.info(
      '[Agenda] id do usuário resolvido via localStorage["idUsuario"]:',
      bruto,
    );
    return Number(bruto);
  }

  console.warn(
    "[Agenda] Não foi possível resolver o id do usuário logado — o botão de confirmar presença não vai aparecer. Ajuste obterIdUsuarioLogado().",
  );
  return null;
}

// Combina classes do CSS Module, ignorando valores falsy.
// Ex: cx(styles['agenda-chip'], ativo && styles['agenda-chip--ativo'])
function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

async function apiFetch(path: string, options: RequestInit = {}) {
  const token = getToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  if (response.status === 204) return null;

  const text = await response.text();
  const data = text ? safeJsonParse(text) : null;

  if (!response.ok) {
    throw new Error(
      typeof data === "string" ? data : data?.message || "Erro na requisição",
    );
  }

  return data;
}

function safeJsonParse(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// Alguns endpoints antigos do projeto serializam listas como
// { "$id": "1", "$values": [...] } (Newtonsoft PreserveReferencesHandling)
// em vez de um array puro. Isso normaliza os dois formatos para sempre virar um array.
function toArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (
    data &&
    typeof data === "object" &&
    Array.isArray((data as any).$values)
  ) {
    return (data as any).$values as T[];
  }
  return [];
}

// ===================== Tipos =====================

interface Usuario {
  id: number;
  nome: string;
  login: string;
  email: string;
}

interface Participante {
  id: number;
  idEvento: number;
  idUsuario: number;
  nome: string;
  login: string;
  email: string;
  confirmado: boolean;
  dataConvite: string;
  dataConfirmacao: string | null;
}

interface EventoAgenda {
  id: number;
  titulo: string;
  descricao: string | null;
  dataInicio: string;
  dataFim: string;
  tipo: "presencial" | "online";
  local: string | null;
  linkReuniao: string | null;
  cor: string | null;
  idUsuarioCriador: number;
  nomeUsuarioCriador: string;
  dataCriacao: string;
  ativo: boolean;
  participantes: Participante[];
}

// tipo possíveis: "criacao" | "edicao" | "extensao_horario" | "novo_link" |
// "acesso_link" | "confirmacao_presenca" | "cancelamento_presenca"
interface HistoricoAgenda {
  id: number;
  idEvento: number;
  idUsuario: number | null;
  nomeUsuario: string | null;
  tipo: string;
  dataHora: string;
  valorAnterior: string | null;
  valorNovo: string | null;
  observacao: string | null;
}

interface EstenderForm {
  novaData: string;
  novaHora: string;
  novoLink: string;
}

interface NovoEventoForm {
  titulo: string;
  descricao: string;
  data: string;
  horaInicio: string;
  horaFim: string;
  tipo: "presencial" | "online";
  local: string;
  linkReuniao: string;
  participantes: number[];
}

const FORM_INICIAL: NovoEventoForm = {
  titulo: "",
  descricao: "",
  data: "",
  horaInicio: "09:00",
  horaFim: "10:00",
  tipo: "presencial",
  local: "",
  linkReuniao: "",
  participantes: [],
};

const NOMES_MES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];
const NOMES_DIA_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function mesmoDia(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function gerarMatrizMes(referencia: Date): Date[] {
  const ano = referencia.getFullYear();
  const mes = referencia.getMonth();

  const primeiroDia = new Date(ano, mes, 1);
  const inicioGrid = new Date(primeiroDia);
  inicioGrid.setDate(primeiroDia.getDate() - primeiroDia.getDay());

  const dias: Date[] = [];
  const cursor = new Date(inicioGrid);
  for (let i = 0; i < 42; i++) {
    dias.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dias;
}

function formatarHora(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function formatarDataCompleta(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatarDataHora(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function combinarDataHoraLocal(data: string, hora: string): string {
  return `${data}T${hora}:00`;
}

// Monta a frase de cada linha do histórico a partir do tipo + valores registrados.
function descreverHistorico(item: HistoricoAgenda): string {
  const quem = item.nomeUsuario || "Alguém";
  switch (item.tipo) {
    case "criacao":
      return `${quem} criou o evento`;
    case "edicao":
      return `${quem} editou o evento`;
    case "extensao_horario":
      return `${quem} estendeu o horário${
        item.valorAnterior && item.valorNovo
          ? ` de ${item.valorAnterior} para ${item.valorNovo}`
          : ""
      }`;
    case "novo_link":
      return `${quem} atualizou o link da reunião`;
    case "acesso_link":
      return `${quem} acessou o link da reunião`;
    case "confirmacao_presenca":
      return `${quem} confirmou presença`;
    case "cancelamento_presenca":
      return `${quem} desmarcou presença`;
    default:
      return `${quem} — ${item.tipo}`;
  }
}

function formatarDataISO(d: Date): string {
  const ano = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
}

// ===================== Componente principal =====================

export default function AgendaPage() {
  const [mesAtual, setMesAtual] = useState(() => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  });

  const token = useSelector((state: RootState) => state.auth.token);
  const usuario = useSelector((state: RootState) => state.auth.user);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useSessionExpirationCheck(sessionExpiration, token);

  const [eventos, setEventos] = useState<EventoAgenda[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [eventoSelecionado, setEventoSelecionado] =
    useState<EventoAgenda | null>(null);
  const [popover, setPopover] = useState<{
    evento: EventoAgenda;
    x: number;
    y: number;
  } | null>(null);
  const fecharPopoverTimeout = useRef<number | null>(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [form, setForm] = useState<NovoEventoForm>(FORM_INICIAL);
  const [buscaParticipante, setBuscaParticipante] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState<string | null>(null);

  const [historico, setHistorico] = useState<HistoricoAgenda[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [eventoEditandoId, setEventoEditandoId] = useState<number | null>(null);

  const [modalEstenderAberto, setModalEstenderAberto] = useState(false);
  const [formEstender, setFormEstender] = useState<EstenderForm>({
    novaData: "",
    novaHora: "",
    novoLink: "",
  });
  const [salvandoEstender, setSalvandoEstender] = useState(false);
  const [erroEstender, setErroEstender] = useState<string | null>(null);

  const usuarioLogadoId = useMemo(() => obterIdUsuarioLogado(), []);

  const dias = useMemo(() => gerarMatrizMes(mesAtual), [mesAtual]);
  const hoje = new Date();

  const carregarEventos = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const inicio = dias[0];
      const fim = new Date(dias[dias.length - 1]);
      fim.setDate(fim.getDate() + 1);

      const qs = new URLSearchParams({
        dataInicio: combinarDataHoraLocal(formatarDataISO(inicio), "00:00"),
        dataFim: combinarDataHoraLocal(formatarDataISO(fim), "00:00"),
      });

      const data = await apiFetch(`/Agenda/GetEventosPeriodo?${qs.toString()}`);
      const eventosNormalizados = toArray<EventoAgenda>(data).map((evento) => ({
        ...evento,
        participantes: toArray<Participante>(evento.participantes),
      }));
      setEventos(eventosNormalizados);
    } catch (e: any) {
      setErro(e.message || "Não foi possível carregar os eventos");
    } finally {
      setCarregando(false);
    }
  }, [dias]);

  useEffect(() => {
    carregarEventos();
  }, [carregarEventos]);

  useEffect(() => {
    apiFetch(
      "/Usuario/GetUsuariosAll?id=0&nome=&login=&email=&nivel=&status=&orderBy=nome&order=asc",
    )
      .then((data) => setUsuarios(toArray<Usuario>(data)))
      .catch(() => setUsuarios([]));
  }, []);

  useEffect(() => {
    if (!eventoSelecionado) {
      setHistorico([]);
      setHistoricoAberto(false);
      return;
    }

    setCarregandoHistorico(true);
    apiFetch(`/Agenda/GetHistorico/${eventoSelecionado.id}`)
      .then((data) => setHistorico(toArray<HistoricoAgenda>(data)))
      .catch(() => setHistorico([]))
      .finally(() => setCarregandoHistorico(false));
  }, [eventoSelecionado?.id]);

  function abrirModalEdicao(evento: EventoAgenda) {
    const inicio = new Date(evento.dataInicio);
    const fim = new Date(evento.dataFim);

    setEventoEditandoId(evento.id);
    setForm({
      titulo: evento.titulo,
      descricao: evento.descricao || "",
      data: formatarDataISO(inicio),
      horaInicio: `${String(inicio.getHours()).padStart(2, "0")}:${String(
        inicio.getMinutes(),
      ).padStart(2, "0")}`,
      horaFim: `${String(fim.getHours()).padStart(2, "0")}:${String(
        fim.getMinutes(),
      ).padStart(2, "0")}`,
      tipo: evento.tipo,
      local: evento.local || "",
      linkReuniao: evento.linkReuniao || "",
      participantes: evento.participantes.map((p) => p.idUsuario),
    });
    setErroForm(null);
    setBuscaParticipante("");
    setEventoSelecionado(null); // fecha o modal de detalhes
    setModalAberto(true); // abre o modal de formulário, agora em modo edição
  }

  function fecharModalEvento() {
    setModalAberto(false);
    setEventoEditandoId(null);
    setForm(FORM_INICIAL);
    setErroForm(null);
    setBuscaParticipante("");
  }

  const eventosPorDia = useMemo(() => {
    const mapa = new Map<string, EventoAgenda[]>();
    for (const evento of eventos) {
      const chave = new Date(evento.dataInicio).toDateString();
      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave)!.push(evento);
    }
    for (const lista of mapa.values()) {
      lista.sort(
        (a, b) =>
          new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime(),
      );
    }
    return mapa;
  }, [eventos]);

  function irParaMesAnterior() {
    setMesAtual(
      (atual) => new Date(atual.getFullYear(), atual.getMonth() - 1, 1),
    );
  }

  function irParaProximoMes() {
    setMesAtual(
      (atual) => new Date(atual.getFullYear(), atual.getMonth() + 1, 1),
    );
  }

  function irParaHoje() {
    const h = new Date();
    setMesAtual(new Date(h.getFullYear(), h.getMonth(), 1));
  }

  function abrirPopover(evento: EventoAgenda, elemento: HTMLElement) {
    if (fecharPopoverTimeout.current)
      window.clearTimeout(fecharPopoverTimeout.current);
    const rect = elemento.getBoundingClientRect();
    setPopover({ evento, x: rect.left + rect.width / 2, y: rect.bottom });
  }

  function agendarFechamentoPopover() {
    fecharPopoverTimeout.current = window.setTimeout(
      () => setPopover(null),
      150,
    );
  }

  function cancelarFechamentoPopover() {
    if (fecharPopoverTimeout.current)
      window.clearTimeout(fecharPopoverTimeout.current);
  }

  function abrirModalNovoEvento(diaClicado?: Date) {
    const base = diaClicado || new Date();
    setEventoEditandoId(null);
    setForm({
      ...FORM_INICIAL,
      data: formatarDataISO(base),
    });
    setErroForm(null);
    setBuscaParticipante("");
    setModalAberto(true);
  }

  function alternarParticipante(idUsuario: number) {
    setForm((atual) => {
      const jaSelecionado = atual.participantes.includes(idUsuario);
      return {
        ...atual,
        participantes: jaSelecionado
          ? atual.participantes.filter((id) => id !== idUsuario)
          : [...atual.participantes, idUsuario],
      };
    });
  }

  async function salvarEvento(e: React.FormEvent) {
    e.preventDefault();
    setErroForm(null);

    if (!form.titulo.trim()) {
      setErroForm("Informe um título para o evento");
      return;
    }
    if (form.tipo === "online" && !form.linkReuniao.trim()) {
      setErroForm("Informe o link da reunião");
      return;
    }
    if (form.tipo === "presencial" && !form.local.trim()) {
      setErroForm("Informe o local do evento");
      return;
    }

    const dataInicioComparacao = new Date(`${form.data}T${form.horaInicio}:00`);
    const dataFimComparacao = new Date(`${form.data}T${form.horaFim}:00`);

    if (dataFimComparacao <= dataInicioComparacao) {
      setErroForm("O horário final deve ser depois do horário inicial");
      return;
    }

    const payload = {
      titulo: form.titulo,
      descricao: form.descricao || null,
      dataInicio: combinarDataHoraLocal(form.data, form.horaInicio),
      dataFim: combinarDataHoraLocal(form.data, form.horaFim),
      tipo: form.tipo,
      local: form.tipo === "presencial" ? form.local : null,
      linkReuniao: form.tipo === "online" ? form.linkReuniao : null,
      participantes: form.participantes,
    };

    setSalvando(true);
    try {
      if (eventoEditandoId) {
        await apiFetch(`/Agenda/${eventoEditandoId}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch("/Agenda", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      fecharModalEvento();
      await carregarEventos();
    } catch (e: any) {
      setErroForm(e.message || "Não foi possível salvar o evento");
    } finally {
      setSalvando(false);
    }
  }

  async function excluirEvento(evento: EventoAgenda) {
    if (
      !window.confirm(
        `Deseja excluir o evento "${evento.titulo}"? Essa ação não pode ser desfeita.`,
      )
    )
      return;

    try {
      await apiFetch(`/Agenda/${evento.id}`, { method: "DELETE" });
      setEventoSelecionado(null);
      await carregarEventos();
    } catch (e: any) {
      setErro(e.message || "Não foi possível excluir o evento");
    }
  }

  async function alternarPresenca(evento: EventoAgenda, confirmar: boolean) {
    try {
      await apiFetch(
        `/Agenda/ConfirmarPresenca/${evento.id}?confirmar=${confirmar}`,
        { method: "PUT" },
      );
      await carregarEventos();
      setEventoSelecionado((atual) => {
        if (!atual || atual.id !== evento.id) return atual;
        const atualizado = { ...atual };
        atualizado.participantes = atualizado.participantes.map((p) =>
          p.idUsuario === usuarioLogadoId ? { ...p, confirmado: confirmar } : p,
        );
        return atualizado;
      });
    } catch (e) {
      // silencioso — o botão volta ao estado anterior no próximo carregamento
    }
  }

  function abrirModalEstender() {
    if (!eventoSelecionado) return;
    const fimAtual = new Date(eventoSelecionado.dataFim);
    setFormEstender({
      novaData: fimAtual.toISOString().slice(0, 10),
      novaHora: fimAtual.toTimeString().slice(0, 5),
      novoLink: "",
    });
    setErroEstender(null);
    setModalEstenderAberto(true);
  }

  async function salvarExtensao(e: React.FormEvent) {
    e.preventDefault();
    if (!eventoSelecionado) return;
    setErroEstender(null);

    const novaDataFimComparacao = new Date(
      `${formEstender.novaData}T${formEstender.novaHora}:00`,
    );

    if (Number.isNaN(novaDataFimComparacao.getTime())) {
      setErroEstender("Informe data e horário válidos");
      return;
    }
    if (novaDataFimComparacao <= new Date(eventoSelecionado.dataInicio)) {
      setErroEstender(
        "O novo horário de término deve ser depois do início do evento",
      );
      return;
    }

    setSalvandoEstender(true);
    try {
      await apiFetch(`/Agenda/EstenderReuniao/${eventoSelecionado.id}`, {
        method: "PUT",
        body: JSON.stringify({
          novaDataFim: combinarDataHoraLocal(
            formEstender.novaData,
            formEstender.novaHora,
          ),
          novoLink:
            eventoSelecionado.tipo === "online" && formEstender.novoLink.trim()
              ? formEstender.novoLink.trim()
              : null,
        }),
      });

      setModalEstenderAberto(false);
      await carregarEventos();

      const eventoAtualizado = await apiFetch(
        `/Agenda/${eventoSelecionado.id}`,
      );
      if (eventoAtualizado) {
        setEventoSelecionado({
          ...eventoAtualizado,
          participantes: toArray<Participante>(eventoAtualizado.participantes),
        });
      }

      const historicoAtualizado = await apiFetch(
        `/Agenda/GetHistorico/${eventoSelecionado.id}`,
      );
      setHistorico(toArray<HistoricoAgenda>(historicoAtualizado));
    } catch (e: any) {
      setErroEstender(e.message || "Não foi possível estender a reunião");
    } finally {
      setSalvandoEstender(false);
    }
  }

  async function acessarLinkReuniao(evento: EventoAgenda) {
    if (!evento.linkReuniao) return;
    try {
      await apiFetch(`/Agenda/RegistrarAcessoLink/${evento.id}`, {
        method: "POST",
      });
    } catch (e) {
      // não bloqueia o acesso à reunião se o log falhar
    } finally {
      window.open(evento.linkReuniao, "_blank", "noopener");
      const historicoAtualizado = await apiFetch(
        `/Agenda/GetHistorico/${evento.id}`,
      ).catch(() => null);
      if (historicoAtualizado) {
        setHistorico(toArray<HistoricoAgenda>(historicoAtualizado));
      }
    }
  }

  const usuariosFiltrados = usuarios.filter((u) =>
    u.nome.toLowerCase().includes(buscaParticipante.toLowerCase()),
  );

  return (
    <div className={styles["agenda-page"]}>
      <header className={styles["agenda-header"]}>
        <div className={styles["agenda-header__titulo"]}>
          <span className={styles["agenda-header__eyebrow"]}>Agenda</span>
          <h1>
            {NOMES_MES[mesAtual.getMonth()]}{" "}
            <span className={styles["agenda-header__ano"]}>
              {mesAtual.getFullYear()}
            </span>
          </h1>
        </div>

        <div className={styles["agenda-header__acoes"]}>
          <div className={styles["agenda-nav"]}>
            <button
              className={styles["agenda-nav__btn"]}
              onClick={irParaMesAnterior}
              aria-label="Mês anterior"
            >
              ‹
            </button>
            <button className={styles["agenda-nav__hoje"]} onClick={irParaHoje}>
              Hoje
            </button>
            <button
              className={styles["agenda-nav__btn"]}
              onClick={irParaProximoMes}
              aria-label="Próximo mês"
            >
              ›
            </button>
          </div>

          <button
            className={styles["agenda-btn-primario"]}
            onClick={() => abrirModalNovoEvento()}
          >
            + Novo evento
          </button>
        </div>
      </header>

      {erro && <div className={styles["agenda-alerta-erro"]}>{erro}</div>}

      <div className={styles["agenda-grid"]}>
        <div className={styles["agenda-grid__semana"]}>
          {NOMES_DIA_SEMANA.map((dia) => (
            <div key={dia} className={styles["agenda-grid__dia-semana"]}>
              {dia}
            </div>
          ))}
        </div>

        <div
          className={cx(
            styles["agenda-grid__corpo"],
            carregando && styles["agenda-grid__corpo--carregando"],
          )}
        >
          {dias.map((dia, index) => {
            const foraDoMes = dia.getMonth() !== mesAtual.getMonth();
            const ehHoje = mesmoDia(dia, hoje);
            const eventosDoDia = eventosPorDia.get(dia.toDateString()) || [];
            const visiveis = eventosDoDia.slice(0, 3);
            const restantes = eventosDoDia.length - visiveis.length;

            return (
              <div
                key={index}
                className={cx(
                  styles["agenda-celula"],
                  foraDoMes && styles["agenda-celula--fora-do-mes"],
                  ehHoje && styles["agenda-celula--hoje"],
                )}
                onDoubleClick={() => abrirModalNovoEvento(dia)}
              >
                <div className={styles["agenda-celula__numero"]}>
                  {ehHoje ? (
                    <span className={styles["agenda-celula__badge-hoje"]}>
                      {dia.getDate()}
                    </span>
                  ) : (
                    dia.getDate()
                  )}
                </div>

                <div className={styles["agenda-celula__eventos"]}>
                  {visiveis.map((evento) => (
                    <button
                      key={evento.id}
                      className={cx(
                        styles["agenda-chip"],
                        styles[`agenda-chip--${evento.tipo}`],
                      )}
                      onMouseEnter={(e) =>
                        abrirPopover(evento, e.currentTarget)
                      }
                      onMouseLeave={agendarFechamentoPopover}
                      onClick={() => setEventoSelecionado(evento)}
                    >
                      <span className={styles["agenda-chip__hora"]}>
                        {formatarHora(evento.dataInicio)}
                      </span>
                      <span className={styles["agenda-chip__titulo"]}>
                        {evento.titulo}
                      </span>
                    </button>
                  ))}

                  {restantes > 0 && (
                    <button
                      className={cx(
                        styles["agenda-chip"],
                        styles["agenda-chip--mais"],
                      )}
                      onClick={() => setEventoSelecionado(eventosDoDia[0])}
                    >
                      +{restantes} evento{restantes > 1 ? "s" : ""}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===================== Popover de hover ===================== */}
      {popover && (
        <div
          className={styles["agenda-popover"]}
          style={{ left: popover.x, top: popover.y }}
          onMouseEnter={cancelarFechamentoPopover}
          onMouseLeave={agendarFechamentoPopover}
        >
          <div className={styles["agenda-popover__header"]}>
            <span
              className={cx(
                styles["agenda-popover__tag"],
                styles[`agenda-popover__tag--${popover.evento.tipo}`],
              )}
            >
              {popover.evento.tipo === "online" ? "Online" : "Presencial"}
            </span>
            <strong>{popover.evento.titulo}</strong>
          </div>

          <div className={styles["agenda-popover__horario"]}>
            {formatarHora(popover.evento.dataInicio)} —{" "}
            {formatarHora(popover.evento.dataFim)}
          </div>

          <div className={styles["agenda-popover__participantes"]}>
            {popover.evento.participantes.length === 0 && (
              <span className={styles["agenda-popover__vazio"]}>
                Nenhum participante convidado
              </span>
            )}
            {popover.evento.participantes.slice(0, 5).map((p) => (
              <div
                key={p.id}
                className={styles["agenda-popover__participante"]}
              >
                <span
                  className={cx(
                    styles["agenda-popover__status"],
                    p.confirmado &&
                      styles["agenda-popover__status--confirmado"],
                  )}
                />
                {p.nome}
              </div>
            ))}
            {popover.evento.participantes.length > 5 && (
              <div className={styles["agenda-popover__mais-participantes"]}>
                +{popover.evento.participantes.length - 5} outros
              </div>
            )}
          </div>

          <button
            className={styles["agenda-popover__ver-mais"]}
            onClick={() => setEventoSelecionado(popover.evento)}
          >
            Ver mais →
          </button>
        </div>
      )}

      {/* ===================== Modal de detalhes do evento ===================== */}
      {eventoSelecionado && (
        <div
          className={styles["agenda-modal-overlay"]}
          onClick={() => setEventoSelecionado(null)}
        >
          <div
            className={styles["agenda-modal"]}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles["agenda-modal__fechar"]}
              onClick={() => setEventoSelecionado(null)}
              aria-label="Fechar"
            >
              ×
            </button>

            <span
              className={cx(
                styles["agenda-popover__tag"],
                styles[`agenda-popover__tag--${eventoSelecionado.tipo}`],
              )}
            >
              {eventoSelecionado.tipo === "online" ? "Online" : "Presencial"}
            </span>

            <h2 className={styles["agenda-modal__titulo"]}>
              {eventoSelecionado.titulo}
            </h2>
            <p className={styles["agenda-modal__data"]}>
              {formatarDataCompleta(eventoSelecionado.dataInicio)}
            </p>
            <p className={styles["agenda-modal__horario"]}>
              {formatarHora(eventoSelecionado.dataInicio)} —{" "}
              {formatarHora(eventoSelecionado.dataFim)}
            </p>

            {eventoSelecionado.descricao && (
              <p className={styles["agenda-modal__descricao"]}>
                {eventoSelecionado.descricao}
              </p>
            )}

            {eventoSelecionado.tipo === "presencial" &&
              eventoSelecionado.local && (
                <div className={styles["agenda-modal__info-linha"]}>
                  <span className={styles["agenda-modal__info-label"]}>
                    Local
                  </span>
                  <span>{eventoSelecionado.local}</span>
                </div>
              )}

            {eventoSelecionado.tipo === "online" &&
              eventoSelecionado.linkReuniao && (
                <div className={styles["agenda-modal__info-linha"]}>
                  <span className={styles["agenda-modal__info-label"]}>
                    Link da reunião
                  </span>
                  <a
                    href={eventoSelecionado.linkReuniao}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      acessarLinkReuniao(eventoSelecionado);
                    }}
                  >
                    {eventoSelecionado.linkReuniao}
                  </a>
                </div>
              )}

            <div className={styles["agenda-modal__info-linha"]}>
              <span className={styles["agenda-modal__info-label"]}>
                Organizador
              </span>
              <span>{eventoSelecionado.nomeUsuarioCriador}</span>
            </div>

            {usuarioLogadoId != null &&
              eventoSelecionado.idUsuarioCriador === usuarioLogadoId && (
                <div className={styles["agenda-modal__acoes"]}>
                  <button
                    type="button"
                    className={styles["agenda-btn-secundario"]}
                    onClick={() => abrirModalEdicao(eventoSelecionado)}
                  >
                    ✎ Editar evento
                  </button>
                  <button
                    type="button"
                    className={styles["agenda-btn-secundario"]}
                    onClick={() => excluirEvento(eventoSelecionado)}
                  >
                    🗑 Excluir evento
                  </button>
                </div>
              )}

            <div className={styles["agenda-modal__participantes"]}>
              <div className={styles["agenda-modal__participantes-header"]}>
                <span className={styles["agenda-modal__info-label"]}>
                  Participantes ({eventoSelecionado.participantes.length})
                </span>
                <span className={styles["agenda-modal__confirmados-count"]}>
                  {
                    eventoSelecionado.participantes.filter((p) => p.confirmado)
                      .length
                  }{" "}
                  confirmados
                </span>
              </div>

              <ul className={styles["agenda-modal__lista-participantes"]}>
                {eventoSelecionado.participantes.map((p) => (
                  <li key={p.id}>
                    <span
                      className={cx(
                        styles["agenda-popover__status"],
                        p.confirmado &&
                          styles["agenda-popover__status--confirmado"],
                      )}
                    />
                    <span className={styles["agenda-modal__participante-nome"]}>
                      {p.nome}
                    </span>
                    <span
                      className={styles["agenda-modal__participante-email"]}
                    >
                      {p.email}
                    </span>
                    {p.confirmado && (
                      <span className={styles["agenda-modal__badge-ok"]}>
                        Confirmado
                      </span>
                    )}
                  </li>
                ))}
                {eventoSelecionado.participantes.length === 0 && (
                  <li className={styles["agenda-popover__vazio"]}>
                    Nenhum participante convidado
                  </li>
                )}
              </ul>

              {usuarioLogadoId != null &&
                eventoSelecionado.participantes.some(
                  (p) => p.idUsuario === usuarioLogadoId,
                ) && (
                  <div className={styles["agenda-modal__minha-presenca"]}>
                    {eventoSelecionado.participantes.find(
                      (p) => p.idUsuario === usuarioLogadoId,
                    )?.confirmado ? (
                      <button
                        className={styles["agenda-btn-secundario"]}
                        onClick={() =>
                          alternarPresenca(eventoSelecionado, false)
                        }
                      >
                        Desmarcar presença
                      </button>
                    ) : (
                      <button
                        className={styles["agenda-btn-primario"]}
                        onClick={() =>
                          alternarPresenca(eventoSelecionado, true)
                        }
                      >
                        Confirmar presença
                      </button>
                    )}
                  </div>
                )}

              <button
                type="button"
                className={styles["agenda-modal__estender-btn"]}
                onClick={abrirModalEstender}
              >
                ⏱ Estender reunião / atualizar link
              </button>
              <p className={styles["agenda-modal__aviso"]}>
                {eventoSelecionado.tipo === "online"
                  ? "Use aqui se a reunião passar do horário ou se o link atual tiver vencido."
                  : "Use aqui se a reunião passar do horário previsto."}
              </p>
            </div>

            <div className={styles["agenda-historico"]}>
              <button
                type="button"
                className={styles["agenda-historico__toggle"]}
                onClick={() => setHistoricoAberto((atual) => !atual)}
              >
                <span className={styles["agenda-modal__info-label"]}>
                  Histórico{" "}
                  {historico.length > 0 ? `(${historico.length})` : ""}
                </span>
                <span
                  className={cx(
                    styles["agenda-historico__toggle-seta"],
                    historicoAberto &&
                      styles["agenda-historico__toggle-seta--aberto"],
                  )}
                >
                  ▶
                </span>
              </button>

              {historicoAberto && (
                <>
                  {carregandoHistorico && (
                    <p className={styles["agenda-historico__vazio"]}>
                      Carregando...
                    </p>
                  )}

                  {!carregandoHistorico && historico.length === 0 && (
                    <p className={styles["agenda-historico__vazio"]}>
                      Nenhum registro ainda
                    </p>
                  )}

                  {!carregandoHistorico && historico.length > 0 && (
                    <ul className={styles["agenda-historico__lista"]}>
                      {historico.map((item) => (
                        <li
                          key={item.id}
                          className={styles["agenda-historico__item"]}
                        >
                          <span
                            className={cx(
                              styles["agenda-historico__marcador"],
                              item.tipo === "acesso_link" &&
                                styles["agenda-historico__marcador--acesso"],
                              item.tipo === "novo_link" &&
                                styles["agenda-historico__marcador--link"],
                            )}
                          />
                          <div className={styles["agenda-historico__corpo"]}>
                            <div className={styles["agenda-historico__texto"]}>
                              {descreverHistorico(item)}
                            </div>
                            <div className={styles["agenda-historico__meta"]}>
                              {formatarDataHora(item.dataHora)}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===================== Modal de estender reunião ===================== */}
      {modalEstenderAberto && eventoSelecionado && (
        <div
          className={styles["agenda-modal-overlay"]}
          onClick={() => setModalEstenderAberto(false)}
        >
          <form
            className={styles["agenda-modal"]}
            onClick={(e) => e.stopPropagation()}
            onSubmit={salvarExtensao}
          >
            <button
              type="button"
              className={styles["agenda-modal__fechar"]}
              onClick={() => setModalEstenderAberto(false)}
              aria-label="Fechar"
            >
              ×
            </button>

            <h2 className={styles["agenda-modal__titulo"]}>Estender reunião</h2>
            <p className={styles["agenda-modal__aviso"]}>
              Término atual: {formatarHora(eventoSelecionado.dataFim)}
            </p>

            {erroEstender && (
              <div className={styles["agenda-alerta-erro"]}>{erroEstender}</div>
            )}

            <div className={styles["agenda-campo-linha"]}>
              <label className={styles["agenda-campo"]}>
                <span>Data</span>
                <input
                  type="date"
                  value={formEstender.novaData}
                  onChange={(e) =>
                    setFormEstender({
                      ...formEstender,
                      novaData: e.target.value,
                    })
                  }
                  required
                />
              </label>
              <label className={styles["agenda-campo"]}>
                <span>Novo término</span>
                <input
                  type="time"
                  value={formEstender.novaHora}
                  onChange={(e) =>
                    setFormEstender({
                      ...formEstender,
                      novaHora: e.target.value,
                    })
                  }
                  required
                />
              </label>
            </div>

            {eventoSelecionado.tipo === "online" && (
              <label className={styles["agenda-campo"]}>
                <span>Novo link (opcional — só se o atual venceu)</span>
                <input
                  type="url"
                  value={formEstender.novoLink}
                  onChange={(e) =>
                    setFormEstender({
                      ...formEstender,
                      novoLink: e.target.value,
                    })
                  }
                  placeholder="https://meet.google.com/..."
                />
              </label>
            )}

            <div className={styles["agenda-modal__acoes"]}>
              <button
                type="button"
                className={styles["agenda-btn-secundario"]}
                onClick={() => setModalEstenderAberto(false)}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={styles["agenda-btn-primario"]}
                disabled={salvandoEstender}
              >
                {salvandoEstender ? "Salvando..." : "Salvar extensão"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ===================== Modal de novo evento ===================== */}
      {modalAberto && (
        <div
          className={styles["agenda-modal-overlay"]}
          onClick={() => fecharModalEvento()}
        >
          <form
            className={cx(styles["agenda-modal"], styles["agenda-modal--form"])}
            onClick={(e) => e.stopPropagation()}
            onSubmit={salvarEvento}
          >
            <button
              type="button"
              className={styles["agenda-modal__fechar"]}
              onClick={() => fecharModalEvento()}
              aria-label="Fechar"
            >
              ×
            </button>

            <h2 className={styles["agenda-modal__titulo"]}>
              {eventoEditandoId ? "Editar evento" : "Novo evento"}{" "}
              {/* título dinâmico */}
            </h2>

            {erroForm && (
              <div className={styles["agenda-alerta-erro"]}>{erroForm}</div>
            )}

            <label className={styles["agenda-campo"]}>
              <span>Título</span>
              <input
                type="text"
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                placeholder="Ex: Reunião de alinhamento com o time"
                required
              />
            </label>

            <label className={styles["agenda-campo"]}>
              <span>Descrição</span>
              <textarea
                value={form.descricao}
                onChange={(e) =>
                  setForm({ ...form, descricao: e.target.value })
                }
                placeholder="Pauta, contexto ou observações (opcional)"
                rows={3}
              />
            </label>

            <div className={styles["agenda-campo-linha"]}>
              <label className={styles["agenda-campo"]}>
                <span>Data</span>
                <input
                  type="date"
                  value={form.data}
                  onChange={(e) => setForm({ ...form, data: e.target.value })}
                  required
                />
              </label>
              <label className={styles["agenda-campo"]}>
                <span>Início</span>
                <input
                  type="time"
                  value={form.horaInicio}
                  onChange={(e) =>
                    setForm({ ...form, horaInicio: e.target.value })
                  }
                  required
                />
              </label>
              <label className={styles["agenda-campo"]}>
                <span>Fim</span>
                <input
                  type="time"
                  value={form.horaFim}
                  onChange={(e) =>
                    setForm({ ...form, horaFim: e.target.value })
                  }
                  required
                />
              </label>
            </div>

            <div className={styles["agenda-campo"]}>
              <span>Formato</span>
              <div className={styles["agenda-tipo-toggle"]}>
                <button
                  type="button"
                  className={cx(
                    styles["agenda-tipo-toggle__opcao"],
                    form.tipo === "presencial" &&
                      styles["agenda-tipo-toggle__opcao--ativo"],
                  )}
                  onClick={() => setForm({ ...form, tipo: "presencial" })}
                >
                  Presencial
                </button>
                <button
                  type="button"
                  className={cx(
                    styles["agenda-tipo-toggle__opcao"],
                    styles["agenda-tipo-toggle__opcao--online"],
                    form.tipo === "online" &&
                      styles["agenda-tipo-toggle__opcao--ativo"],
                  )}
                  onClick={() => setForm({ ...form, tipo: "online" })}
                >
                  Online
                </button>
              </div>
            </div>

            {form.tipo === "presencial" ? (
              <label className={styles["agenda-campo"]}>
                <span>Local</span>
                <input
                  type="text"
                  value={form.local}
                  onChange={(e) => setForm({ ...form, local: e.target.value })}
                  placeholder="Ex: Sala de reuniões 2, matriz"
                />
              </label>
            ) : (
              <label className={styles["agenda-campo"]}>
                <span>Link da reunião</span>
                <input
                  type="url"
                  value={form.linkReuniao}
                  onChange={(e) =>
                    setForm({ ...form, linkReuniao: e.target.value })
                  }
                  placeholder="https://meet.google.com/..."
                />
              </label>
            )}

            <div className={styles["agenda-campo"]}>
              <span>
                Participantes ({form.participantes.length} selecionados)
              </span>
              <input
                type="text"
                className={styles["agenda-busca-participante"]}
                placeholder="Buscar usuário..."
                value={buscaParticipante}
                onChange={(e) => setBuscaParticipante(e.target.value)}
              />
              <div className={styles["agenda-lista-participantes"]}>
                {usuariosFiltrados.map((u) => (
                  <label
                    key={u.id}
                    className={styles["agenda-lista-participantes__item"]}
                  >
                    <input
                      type="checkbox"
                      checked={form.participantes.includes(u.id)}
                      onChange={() => alternarParticipante(u.id)}
                    />
                    <span>{u.nome}</span>
                    <span
                      className={styles["agenda-lista-participantes__email"]}
                    >
                      {u.email}
                    </span>
                  </label>
                ))}
                {usuariosFiltrados.length === 0 && (
                  <div className={styles["agenda-popover__vazio"]}>
                    Nenhum usuário encontrado
                  </div>
                )}
              </div>
            </div>

            <div className={styles["agenda-modal__acoes"]}>
              <button
                type="button"
                className={styles["agenda-btn-secundario"]}
                onClick={() => fecharModalEvento()}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={styles["agenda-btn-primario"]}
                disabled={salvando}
              >
                {salvando
                  ? "Salvando..."
                  : eventoEditandoId
                    ? "Salvar alterações"
                    : "Criar evento"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
